import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  RadioGroup,
  Radio,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';

const CHURN_REASONS = [
  'Me suscribí por error',
  'No encuentro suficiente valor o ahorro en los beneficios actuales',
  'No entiendo el valor de la suscripción',
  'He tenido errores constantes en la plataforma',
  'Otro',
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
};

const SubscriptionCancellationModal = ({ isOpen, onClose, onConfirm }: Props): JSX.Element => {
  const [step, setStep] = useState<'reason' | 'confirm'>('reason');
  const [selectedReason, setSelectedReason] = useState('');
  const [otherText, setOtherText] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  const isOther = selectedReason === 'Otro';
  const effectiveReason = isOther ? otherText.trim() : selectedReason;
  const canContinue = !!selectedReason && (!isOther || !!otherText.trim());
  const leastDestructiveRef = useRef<HTMLButtonElement>(null);

  const handleClose = () => {
    onClose();
    setStep('reason');
    setSelectedReason('');
    setOtherText('');
  };

  const handleReasonContinue = () => {
    setStep('confirm');
  };

  const handleConfirm = async () => {
    setIsCancelling(true);
    try {
      await onConfirm(effectiveReason);
      handleClose();
    } finally {
      setIsCancelling(false);
    }
  };

  const handleBack = () => {
    setStep('reason');
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered initialFocusRef={leastDestructiveRef}>
      <ModalOverlay />
      <ModalContent mx={4}>
        {step === 'reason' && (
          <>
            <ModalHeader fontSize="lg" fontWeight="bold">
              ¿Por qué deseas cancelar?
            </ModalHeader>
            <ModalBody>
              <RadioGroup value={selectedReason} onChange={setSelectedReason}>
                <Stack spacing={3}>
                  {CHURN_REASONS.map((reason) => (
                    <Radio key={reason} value={reason} alignItems="flex-start">
                      <Text fontSize="0.875rem" lineHeight={1.4} mt="-1px">
                        {reason}
                      </Text>
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
              {isOther && (
                <Textarea
                  mt={3}
                  placeholder="Cuéntanos más..."
                  size="sm"
                  borderRadius="md"
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  maxLength={300}
                  resize="none"
                  rows={3}
                />
              )}
            </ModalBody>
            <ModalFooter gap={3}>
              <Button variant="ghost" onClick={handleClose}>
                Volver
              </Button>
              <Button colorScheme="red" onClick={handleReasonContinue} isDisabled={!canContinue}>
                Continuar
              </Button>
            </ModalFooter>
          </>
        )}

        {step === 'confirm' && (
          <>
            <ModalHeader fontSize="lg" fontWeight="bold">
              Cancelar suscripción
            </ModalHeader>
            <ModalBody>
              <Text fontSize="0.875rem">
                ¿Estás seguro de que deseas cancelar esta suscripción? Podrás volver a activarla en tu próximo pago.
              </Text>
            </ModalBody>
            <ModalFooter gap={3}>
              <Button ref={leastDestructiveRef} variant="ghost" onClick={handleBack}>
                Volver
              </Button>
              <Button colorScheme="red" onClick={handleConfirm} isLoading={isCancelling}>
                Sí, cancelar
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default SubscriptionCancellationModal;
